import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scientistService } from '../../services/scientistService';
import { 
  BarChart3, 
  MapPin, 
  Users, 
  TrendingUp, 
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ScientistDashboard = () => {
  const [selectedView, setSelectedView] = useState('overview');
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({
    totalTrials: 0,
    activeTrials: 0,
    totalLocations: 0,
    assignedPlotOfficers: 0,
    pendingUpdates: 0,
    completedTrials: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [assignedTrials, setAssignedTrials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load dashboard data from API with fallback to mock data
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const [summaryResponse, activitiesResponse, trialsResponse] = await Promise.all([
        scientistService.getDashboardSummary(),
        scientistService.getRecentActivities(),
        scientistService.getAssignedTrials()
      ]);
      
      if (summaryResponse.success) {
        setDashboardStats(summaryResponse.data || summaryResponse);
      }
      
      if (activitiesResponse.success) {
        setRecentActivities(activitiesResponse.data || activitiesResponse);
      }

      if (trialsResponse.success) {
        setAssignedTrials(trialsResponse.data || trialsResponse);
      }
    } catch (error) {
      console.warn('Failed to load dashboard data from API, using mock data:', error.message);
      // Keep existing mock data if API fails
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Fallback mock data for recent activities
  const fallbackActivities = [
    {
      id: 1,
      type: 'trial_created',
      message: 'New trial ART-RICE-2024-KHARIF created',
      timestamp: '2 hours ago',
      status: 'completed'
    },
    {
      id: 2,
      type: 'location_assigned',
      message: 'Assigned 3 locations to Plot Officer John Doe',
      timestamp: '1 day ago',
      status: 'completed'
    },
    {
      id: 3,
      type: 'data_received',
      message: 'Received data entry from Plot Officer for Trial ART-MAIZE-2024',
      timestamp: '2 days ago',
      status: 'completed'
    },
    {
      id: 4,
      type: 'trial_update',
      message: 'Updated trial parameters for ART-SUGARCANE-2024',
      timestamp: '3 days ago',
      status: 'completed'
    },
    {
      id: 5,
      type: 'trial_update',
      message: 'Awaiting data from Plot Officer for Trial ART-COTTON-2024',
      timestamp: '5 days ago',
      status: 'active'
    }
  ];

  const displayActivities = recentActivities.length > 0 ? recentActivities : fallbackActivities;

  // Fallback mock data for assigned trials
  const fallbackTrials = [
    {
      id: 1,
      trialCode: 'ARTRICE2024KHARIFA001',
      trialName: 'Advanced Rice Technology Trial',
      crop: 'Rice',
      year: 2024,
      season: 'Kharif',
      status: 'active',
      locations: 5,
      assignedOfficers: 2,
      lastUpdate: '2024-01-15',
      nextUpdate: '2024-01-30'
    },
    {
      id: 2,
      trialCode: 'ARTMAIZE2024KHARIFA002',
      trialName: 'Maize Hybrid Evaluation',
      crop: 'Maize',
      year: 2024,
      season: 'Kharif',
      status: 'active',
      locations: 3,
      assignedOfficers: 1,
      lastUpdate: '2024-01-10',
      nextUpdate: '2024-01-25'
    },
    {
      id: 3,
      trialCode: 'MLTSUGARCANE2024KHARIFM001',
      trialName: 'Sugarcane Multi-Location Trial',
      crop: 'Sugarcane',
      year: 2024,
      season: 'Kharif',
      status: 'active',
      locations: 8,
      assignedOfficers: 3,
      lastUpdate: '2024-01-05',
      nextUpdate: '2024-01-20'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'trial_created': return <CheckCircle className="w-4 h-4" />;
      case 'location_assigned': return <Users className="w-4 h-4" />;
      case 'data_received': return <CheckCircle className="w-4 h-4" />;
      case 'trial_update': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getActivityColor = (status) => {
    return status === 'completed' ? 'text-green-600' : 'text-yellow-600';
  };



  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scientist Dashboard</h1>
          <p className="text-gray-600">Manage trials and monitor research activities</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setSelectedView('overview')}
            className={`px-4 py-2 rounded-lg font-medium ${
              selectedView === 'overview'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Overview
          </button>
        </div>
      </div>

      {selectedView === 'overview' && (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Trials</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalTrials}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Trials</p>
                  <p className="text-2xl font-bold text-green-600">{dashboardStats.activeTrials}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Locations</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalLocations}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Plot Officers</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats.assignedPlotOfficers}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Updates</p>
                  <p className="text-2xl font-bold text-yellow-600">{dashboardStats.pendingUpdates}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-blue-600">{dashboardStats.completedTrials}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          

          {/* Recent Activities */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activities</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                                  {displayActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${getActivityColor(activity.status)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500">{activity.timestamp}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                      {activity.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ScientistDashboard; 